import { BeckyModel, BeckyRequestPayload } from '../../types/becky'
import { BeckyTopology, ModuleFetcher, SiteAssetsHttpClient } from '@wix/thunderbolt-becky-types'
import { getComponentMappers } from './tbElementsSiteAssets'
import { getMergedDataWithFlatPageStructure } from './processedPages'
import { deStringifyExperiments } from './experimentsParser'

export type ResolveBeckyModelParams = {
	payload: BeckyRequestPayload
	httpClient: SiteAssetsHttpClient
	beckyTopology: BeckyTopology
	moduleFetcher?: ModuleFetcher
}

export const resolveBeckyModel = async ({
	payload,
	beckyTopology: { publicTopology, environmentTopology },
	httpClient,
	moduleFetcher,
}: ResolveBeckyModelParams): Promise<BeckyModel> => {
	const {
		pages,
		viewMode,
		beckyExperiments,
		tbElementsSiteAssets,
		isWixCodeOnPage: isWixCodeOnPageParam,
		isWixCodeOnSite: isWixCodeOnSiteParam,
		hasTPAWorkerOnSite: hasTPAWorkerOnSiteParam,
		freemiumBanner: freemiumBannerParam,
		coBrandingBanner: coBrandingBannerParam,
		mobileActionsMenu: mobileActionsMenuParam,
		externalBaseUrl,
		metaSiteId,
		siteId,
		clientSpecMap,
		language,
		originalLanguage,
		isWixSite,
		languageResolutionMethod,
		isMultilingualEnabled,
		useSandboxInHTMLComp,
		routersInfo,
		deviceType,
		qaMode,
		isInSeo: isInSeoParam,
		isPremiumDomain,
		migratingToOoiWidgetIds,
		stylableItemsCss,
		stylableInstance,
		registryLibrariesTopology,
		isHttps,
	} = payload

	const fileRepoUrl = environmentTopology.fileRepoUrl
	const isResponsive = payload.isResponsive === 'true'
	const experiments = deStringifyExperiments(beckyExperiments)

	const [processedPages, componentsMappers] = await Promise.all([
		getMergedDataWithFlatPageStructure(pages, viewMode, isResponsive),
		getComponentMappers({
			moduleFetcher,
			httpClient,
			fileRepoUrl,
			tbElementsSiteAssets,
			experiments,
			registryLibrariesTopology,
		}),
	])

	const isInSeo = isInSeoParam === 'true'

	return {
		...processedPages,
		externalBaseUrl,
		metaSiteId,
		siteId,
		previousStore: new WeakMap(),
		viewMode: viewMode || 'desktop',
		environmentTopology,
		publicTopology,
		language: language!,
		isMultilingualEnabled: isMultilingualEnabled === 'true',
		languageResolutionMethod,
		originalLanguage: originalLanguage || 'en',
		useSandboxInHTMLComp: useSandboxInHTMLComp === 'true',
		isResponsive,
		isWixSite: isWixSite === 'true',
		hasTPAWorkerOnSite: hasTPAWorkerOnSiteParam === 'true',
		fetchRequestsStore: {},
		afterFirstRecalculate: false,
		isWixCodeOnPage: isWixCodeOnPageParam === 'true',
		isWixCodeOnSite: isWixCodeOnSiteParam === 'true',
		renderFlags: {
			isWixAdsAllowed: freemiumBannerParam === 'true',
			showScreenInComp: isInSeo,
			componentViewMode: 'preview',
			componentPreviewStates: {},
			isBackToTopButtonAllowed: false,
			shouldBlockGoogleMapInteraction: false,
			shouldResetTinyMenuZIndex: false,
			shouldResetSlideShowNextPrevButtonsPosition: false,
			shouldResetSubscribeFormMinMaxWidth: false,
			isPlayingAllowed: false,
			isSocialInteractionAllowed: false,
			isExternalNavigationAllowed: true,
			isSlideShowGalleryClickAllowed: true,
			renderFixedPositionContainers: true,
			shouldResetGalleryToOriginalState: false,
			shouldResetComponent: true,
			siteScale: 1,
			isComponentTransitionAllowed: true,
			allowShowingFixedComponents: true,
			isZoomAllowed: false,
			renderFixedPositionBackgrounds: true,
			extraSiteHeight: 0,
			ignoreComponentsHiddenProperty: [],
			ignoreComponentsCollapsedProperty: [],
			showHiddenComponents: false,
			blockingLayer: null,
		},
		shouldShowFreemiumBanner: freemiumBannerParam === 'true',
		shouldShowCoBrandingBanner: coBrandingBannerParam === 'true',
		shouldShowMobileActionsMenu: mobileActionsMenuParam === 'true',
		isPremiumDomain: isPremiumDomain === 'true',
		clientSpecMap,
		componentsMappers,
		experiments,
		routersInfo: routersInfo!,
		deviceType: deviceType!,
		qaMode: qaMode === 'true',
		isInSeo,
		migratingToOoiWidgetIds: migratingToOoiWidgetIds ? migratingToOoiWidgetIds.split(',') : [],
		stylableItemsCss: stylableItemsCss || {},
		stylableInstance,
		themeCounter: 0,
		enableVariantsTransitionsInEditor: true,
		compIdToIgnoredTransformProps: {},
		globalIgnoredTransformProps: [],
		isHttps: isHttps === 'true',
	}
}
